import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import FAQSection from "@/components/FAQSection";
import { MapCard, MapPreview } from "@/components/AnxietyMap";
import { MAPS } from "@/lib/maps";
import { CATEGORIES } from "@/lib/categories";
import { getAllArticles, getArticlesByCategory } from "@/lib/articles";
import {
  SITE,
  REVIEWER,
  SHRINK_NETWORK_URL,
  CARE_ANXIETY_URL,
  NETWORK_MASTER_MESSAGE,
  networkUrl,
} from "@/lib/site";
import { faqSchema, homeMedicalWebPageSchema } from "@/lib/schema";
import PageShareBar from "@/components/PageShareBar";
import { HOMEPAGE_FAQ } from "@/data/homeFaq";

const HOMEPAGE_LAST_REVIEWED = "May 14, 2026";
const HOMEPAGE_LAST_REVIEWED_ISO = "2026-05-14";

const HOMEPAGE_DESCRIPTION =
  "An evidence-based editorial publication about anxiety, panic, and the nervous system, written and medically reviewed by a board-certified psychiatrist. Part of The Shrink Network.";

const START_HERE = [
  {
    title: "Just diagnosed with anxiety: what happens next",
    href: "/resources/just-diagnosed-with-anxiety",
    description:
      "A psychiatrist's practical guide to the first weeks after an anxiety diagnosis: what it means, what to expect from treatment, and what to ask.",
  },
  {
    title: "How to support a partner with anxiety",
    href: "/resources/supporting-a-partner-with-anxiety",
    description:
      "What actually helps when someone you love has anxiety, what tends to backfire, and how to support them without taking it on yourself.",
  },
  {
    title: "How to find a therapist for anxiety",
    href: "/resources/how-to-find-a-therapist",
    description:
      "A step-by-step guide to finding a therapist, from directories and insurance to the first call and telling whether it's a fit.",
  },
  {
    title: "Panic disorder: the overview",
    href: "/conditions/panic-disorder",
    description:
      "What panic disorder is, how recurrent panic attacks form a self-sustaining cycle, and the treatments that break it.",
  },
  {
    title: "Generalized anxiety disorder: the overview",
    href: "/conditions/generalized-anxiety-disorder",
    description:
      "Worry that runs in the background most days, the physical signs that come with it, and how it's diagnosed and treated.",
  },
  {
    title: "Chest tightness from anxiety",
    href: "/symptoms/chest-tightness",
    description:
      "The most common physical symptom of anxiety, what the body is doing during it, and how to tell it apart from a cardiac cause.",
  },
  {
    title: "Why panic attacks end the way they do",
    href: "/panic-nervous-system/why-panic-attacks-end",
    description:
      "Why a panic attack always stops on its own, and what that fact is useful for.",
  },
  {
    title: "How CBT and SSRIs actually work",
    href: "/treatment-education/how-cbt-and-ssris-work",
    description:
      "A plain account of the two best-studied treatments for anxiety, and what they don't do.",
  },
  {
    title: "Finding psychiatric care for anxiety",
    href: "/treatment-education/finding-psychiatric-care",
    description:
      "How to find psychiatric care, what a first evaluation involves, and when medication is worth considering.",
  },
  {
    title: "Exposure therapy, explained",
    href: "/treatment-education/exposure-therapy-explained",
    description:
      "How exposure therapy retrains the fear response, why it works, and what a course of it actually looks like.",
  },
];

interface HomeArticleCard {
  slug: string;
  category: string;
  title: string;
  description: string;
}

const RECENT_GROUPS: { heading: string; slugs: { category: string; slug: string }[] }[] = [
  {
    heading: "From symptoms",
    slugs: [
      { category: "symptoms", slug: "chest-tightness" },
      { category: "symptoms", slug: "insomnia" },
    ],
  },
  {
    heading: "From conditions",
    slugs: [
      { category: "conditions", slug: "generalized-anxiety-disorder" },
      { category: "conditions", slug: "panic-disorder" },
    ],
  },
  {
    heading: "From treatment",
    slugs: [
      { category: "treatment-education", slug: "how-cbt-and-ssris-work" },
      { category: "treatment-education", slug: "finding-psychiatric-care" },
    ],
  },
];

const CONDITION_HOMEPAGE_FEATURED: Array<{ slug: string; lede: string }> = [
  {
    slug: "generalized-anxiety-disorder",
    lede: "Worry that runs in the background more days than not, plus the physical signs that come with it.",
  },
  {
    slug: "panic-disorder",
    lede: "Recurrent unexpected panic attacks, plus the worry between them that keeps the cycle going.",
  },
  {
    slug: "obsessive-compulsive-disorder",
    lede: "Intrusive thoughts the person knows are products of their own mind, paired with rituals that feel necessary.",
  },
];


export default function HomePage() {
  const allArticles = getAllArticles();
  const conditionArticles = getArticlesByCategory("conditions");
  const conditionsFeatured = CONDITION_HOMEPAGE_FEATURED.map((c) => ({
    article: conditionArticles.find((a) => a.slug === c.slug),
    lede: c.lede,
  })).filter((x): x is { article: NonNullable<typeof x.article>; lede: string } =>
    Boolean(x.article),
  );

  // §3 Maps elevation: feature the Panic Map, then the eight cluster maps in
  // the spec's order below it.
  const featuredPanicMap = MAPS.find((m) => m.slug === "panic");
  const CLUSTER_MAP_ORDER = [
    "overthinking",
    "social-anxiety",
    "sleep-and-anxiety",
    "high-performer",
    "health-anxiety",
    "generalized-anxiety",
    "ocd",
    "trauma",
  ];
  const clusterMaps = CLUSTER_MAP_ORDER.map((slug) =>
    MAPS.find((m) => m.slug === slug),
  ).filter((m): m is NonNullable<typeof m> => Boolean(m));

  const recentGroups = RECENT_GROUPS.map((g) => ({
    heading: g.heading,
    cards: g.slugs
      .map(({ category, slug }): HomeArticleCard | null => {
        const a = allArticles.find(
          (x) => x.category === category && x.slug === slug,
        );
        return a
          ? {
              slug: a.slug,
              category: a.category,
              title: a.title,
              description: a.description,
            }
          : null;
      })
      .filter((c): c is HomeArticleCard => c !== null),
  })).filter((g) => g.cards.length > 0);

  return (
    <>
      <Seo
        title="AnxietyResource | Anxiety. Understood."
        description={HOMEPAGE_DESCRIPTION}
        path="/"
        jsonLd={[
          homeMedicalWebPageSchema(HOMEPAGE_LAST_REVIEWED_ISO),
          faqSchema(HOMEPAGE_FAQ),
        ]}
      />

      {/* Hero with image */}
      <section
        className="hero-surface border-b"
        style={{ borderColor: "hsl(var(--border))" }}
      >
        <div className="mx-auto max-w-[1200px] px-6 py-14 md:py-20">
          <div className="grid gap-10 md:grid-cols-2 md:items-center">
            <div>
              <span className="brand-pill mb-4">An editorial publication</span>
              <hr className="brand-rule" />
              <p
                className="font-serif italic text-2xl md:text-3xl leading-tight mb-4"
                style={{ color: "hsl(var(--foreground))" }}
              >
                Anxiety. Understood.
              </p>
              <p
                className="text-base md:text-lg leading-relaxed mb-4"
                style={{ color: "hsl(var(--foreground))" }}
              >
                AnxietyResource is the anxiety-specific layer of{" "}
                <a
                  href={networkUrl(SHRINK_NETWORK_URL, "home_hero")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline decoration-1 underline-offset-2"
                >
                  The Shrink Network
                </a>
                , focused on panic, worry, fear, avoidance, and the nervous
                system.
              </p>
              <h1
                className="font-serif text-4xl md:text-5xl leading-[1.1] tracking-tight"
                style={{ marginTop: 0 }}
              >
                <span className="brand-underline">Anxiety</span>, panic, and the
                nervous system: clear, clinically reviewed information.
              </h1>
              <p
                className="mt-6 text-lg leading-relaxed"
                style={{ color: "hsl(var(--muted-foreground))" }}
              >
                Plain-language explainers written and reviewed by a
                board-certified psychiatrist. No hype. No self-help slogans.
                Useful answers to the questions people actually search for at
                2 a.m.
              </p>

              <div
                className="flex items-center gap-3 mt-6 text-[14px]"
                style={{ color: "hsl(var(--muted-foreground))" }}
              >
                <img
                  src="/brand/refai-headshot.webp"
                  alt="Shariq Refai, MD, MBA"
                  width={40}
                  height={40}
                  loading="eager"
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 999,
                    objectFit: "cover",
                    border: "1px solid hsl(var(--border))",
                  }}
                />
                <span>
                  Reviewed by{" "}
                  <a
                    href={REVIEWER.profileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "hsl(var(--brand-text))", fontWeight: 600 }}
                  >
                    Shariq Refai, MD, MBA
                  </a>
                  {" "}&middot; {REVIEWER.credential} &middot; Last full
                  editorial review: {HOMEPAGE_LAST_REVIEWED}
                </span>
              </div>

              <div className="mt-7 flex flex-wrap gap-3">
                <Link
                  to="/symptoms"
                  className="inline-flex items-center px-4 py-2 text-sm font-semibold hover:no-underline btn-lift"
                  style={{
                    background: "hsl(var(--brand-cta-bg))",
                    color: "hsl(var(--brand-foreground))",
                    borderRadius: "var(--radius)",
                  }}
                  data-testid="cta-symptoms"
                >
                  Start with symptoms
                </Link>
                <Link
                  to="/treatment-education"
                  className="inline-flex items-center px-4 py-2 text-sm font-semibold hover:no-underline btn-lift"
                  style={{
                    border: "1.5px solid hsl(var(--brand))",
                    color: "hsl(var(--brand-text))",
                    background: "transparent",
                    borderRadius: "var(--radius)",
                  }}
                  data-testid="cta-treatment"
                >
                  Treatment &amp; care
                </Link>
              </div>
            </div>
            <figure className="relative">
              <img
                src="/images/home/hero-1280.webp"
                srcSet="/images/home/hero-800.webp 800w, /images/home/hero-1280.webp 1280w"
                sizes="(max-width: 768px) 100vw, 600px"
                alt="Soft morning light on a writing desk with a sage-green plant in the foreground"
                width={1280}
                height={720}
                loading="eager"
                // @ts-expect-error fetchpriority is valid HTML, not yet in React types
                fetchpriority="high"
                decoding="async"
                style={{
                  width: "100%",
                  height: "auto",
                  borderRadius: "var(--radius)",
                  border: "1px solid hsl(var(--border))",
                  display: "block",
                }}
              />
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: -10,
                  bottom: -10,
                  width: 60,
                  height: 60,
                  background: "hsl(var(--brand))",
                  borderRadius: "var(--radius)",
                  zIndex: -1,
                }}
              />
            </figure>
          </div>
          <PageShareBar />
        </div>
      </section>

      {/* Start with what you're experiencing, experiential nav elevated above the fold */}
      <section
        className="border-t"
        style={{ borderColor: "hsl(var(--border))" }}
      >
        <div className="reveal mx-auto max-w-[1200px] px-6 py-14">
          <h2 className="font-serif text-2xl" style={{ marginTop: 0 }}>
            Start with what you're experiencing.
          </h2>
          <p
            className="mt-2 mb-6 text-base leading-relaxed"
            style={{ color: "hsl(var(--muted-foreground))" }}
          >
            A first-time visitor's most useful starting point isn't a diagnosis;
            it's a feeling. Pick whichever one sounds most like yours.
          </p>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {CATEGORIES.map((c) => (
              <Link
                key={c.slug}
                to={`/${c.slug}`}
                className="group card-lift block bg-card p-6 hover:no-underline"
                style={{
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "var(--radius)",
                }}
                data-testid={`card-category-${c.slug}`}
              >
                <span className="brand-pill mb-2">Topic</span>
                <h3
                  className="font-serif text-lg text-foreground"
                  style={{ marginTop: "0.5rem" }}
                >
                  {c.title}
                </h3>
                <p
                  className="mt-2 text-sm leading-relaxed"
                  style={{ color: "hsl(var(--muted-foreground))" }}
                >
                  {c.description}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* What's anxiety? */}
      <section
        className="border-t"
        style={{ borderColor: "hsl(var(--border))" }}
      >
        <div className="reveal mx-auto max-w-[760px] px-6 py-14">
          <h2 className="font-serif text-3xl" style={{ marginTop: 0 }}>
            What's anxiety?
          </h2>
          <div
            className="prose-editorial mt-6 text-[17px] leading-relaxed space-y-5"
          >
            <p>
              Anxiety is the body&rsquo;s stress response, applied to a situation
              that isn't currently dangerous. The same physiological system that
              prepares a person to run from a threat, with faster heart rate,
              tighter chest, sharper attention, redirected blood flow,
              runs in the meeting, the relationship conversation, the bedtime
              quiet. The response is normal. The mismatch between the response
              and the situation is what makes anxiety wear a person down.
            </p>
            <p>
              Clinically, anxiety becomes a disorder when it's persistent, out
              of proportion to the trigger, and interferes with sleep, work,
              relationships, or daily function. The DSM-5-TR recognizes several
              distinct anxiety disorders, including{" "}
              <Link to="/conditions/generalized-anxiety-disorder">
                generalized anxiety disorder
              </Link>
              ,{" "}
              <Link to="/conditions/panic-disorder">panic disorder</Link>,{" "}
              <Link to="/conditions/social-anxiety-disorder">
                social anxiety disorder
              </Link>
              , and{" "}
              <Link to="/conditions/specific-phobias-and-agoraphobia">
                specific phobias and agoraphobia
              </Link>
              . Two related conditions,{" "}
              <Link to="/conditions/obsessive-compulsive-disorder">
                obsessive-compulsive disorder
              </Link>{" "}
              and{" "}
              <Link to="/conditions/post-traumatic-stress-disorder">
                post-traumatic stress disorder
              </Link>
              , sit in adjacent DSM chapters but share much of the
              clinical picture and are covered alongside the anxiety disorders
              here.
            </p>
            <p>
              Most people first notice anxiety in the body rather than in their
              thoughts.{" "}
              <Link to="/symptoms/chest-tightness">Chest tightness</Link>.{" "}
              <Link to="/symptoms/racing-thoughts">Racing thoughts</Link> at
              night.{" "}
              <Link to="/symptoms/nausea-from-anxiety">Nausea</Link> before a
              difficult day. The body has been preparing for an event that may
              or may not arrive, and the preparation itself becomes the symptom.
              Reading these signs as something the nervous system is doing,
              rather than as something gone wrong, is usually the beginning of
              the work.
            </p>
            <p>
              The good news for treatment is that anxiety is among the most
              treatable conditions in psychiatry. Cognitive behavioral therapy,
              particularly with an exposure component, has the strongest
              evidence base. SSRIs and SNRIs work meaningfully for most people
              who try them. Both, in combination, tend to work better than
              either alone for moderate-to-severe cases. The articles in the{" "}
              <Link to="/treatment-education">treatment section</Link> explain
              what each approach actually does.
            </p>
            <p>
              This site is written and reviewed by a board-certified
              psychiatrist for the general public. The articles describe what
              symptoms feel like, what the body is doing during them, and what
              the published evidence says about treatment. Nothing here is a
              substitute for clinical care. It's meant to make the conversation
              with a clinician shorter and more productive.
            </p>
          </div>
        </div>
      </section>

      {/* Start here */}
      <section
        className="border-t"
        style={{
          borderColor: "hsl(var(--border))",
          background: "hsl(var(--secondary) / 0.35)",
        }}
      >
        <div className="reveal mx-auto max-w-[1200px] px-6 py-14">
          <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
            <h2 className="font-serif text-2xl" style={{ marginTop: 0 }}>
              Start here
            </h2>
            <span className="brand-pill">10 essential reads</span>
          </div>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {START_HERE.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className="group card-lift block bg-card p-6 hover:no-underline"
                style={{
                  border: "1px solid hsl(var(--border))",
                  borderTop: "3px solid hsl(var(--brand))",
                  borderRadius: "var(--radius)",
                }}
                data-testid={`card-start-${item.href.replace(/\//g, "-")}`}
              >
                <h3
                  className="font-serif text-lg text-foreground"
                  style={{ marginTop: 0 }}
                >
                  {item.title}
                </h3>
                <p
                  className="mt-2 text-sm leading-relaxed"
                  style={{ color: "hsl(var(--muted-foreground))" }}
                >
                  {item.description}
                </p>
                <span
                  className="inline-block mt-4 text-xs font-semibold"
                  style={{ color: "hsl(var(--brand-text))" }}
                >
                  Read{" "}
                  <span className="link-arrow" aria-hidden="true">
                    &rarr;
                  </span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Recent articles, 6 cards in 3 groups (Audit Pack C: trimmed from 9) */}
      {recentGroups.length > 0 && (
        <section
          className="border-t"
          style={{ borderColor: "hsl(var(--border))" }}
        >
          <div className="reveal mx-auto max-w-[1200px] px-6 py-14">
            <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
              <h2 className="font-serif text-2xl" style={{ marginTop: 0 }}>
                Recent articles
              </h2>
              <Link
                to="/glossary"
                className="text-sm"
                style={{ color: "hsl(var(--brand-text))", fontWeight: 600 }}
              >
                Browse glossary &rarr;
              </Link>
            </div>
            <div className="space-y-10">
              {recentGroups.map((group) => (
                <div key={group.heading}>
                  <h3
                    className="text-xs font-semibold uppercase tracking-widest mb-3"
                    style={{ color: "hsl(var(--accent-ink))" }}
                  >
                    {group.heading}
                  </h3>
                  <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                    {group.cards.map((c) => {
                      const cat = CATEGORIES.find((x) => x.slug === c.category);
                      return (
                        <Link
                          key={c.slug}
                          to={`/${c.category}/${c.slug}`}
                          className="group card-lift block bg-card p-5 hover:no-underline"
                          style={{
                            border: "1px solid hsl(var(--border))",
                            borderRadius: "var(--radius)",
                          }}
                          data-testid={`card-recent-${c.slug}`}
                        >
                          {cat && (
                            <span className="brand-pill mb-2">
                              {cat.shortTitle}
                            </span>
                          )}
                          <h4
                            className="font-serif text-lg text-foreground"
                            style={{ marginTop: "0.5rem" }}
                          >
                            {c.title}
                          </h4>
                          <p
                            className="mt-2 text-sm leading-relaxed"
                            style={{ color: "hsl(var(--muted-foreground))" }}
                          >
                            {c.description}
                          </p>
                          <p
                            className="mt-3 text-xs"
                            style={{ color: "hsl(var(--muted-foreground))" }}
                          >
                            Updated {HOMEPAGE_LAST_REVIEWED}
                          </p>
                          <span
                            className="inline-block mt-3 text-xs font-semibold"
                            style={{ color: "hsl(var(--brand-text))" }}
                          >
                            Read{" "}
                  <span className="link-arrow" aria-hidden="true">
                    &rarr;
                  </span>
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-10 text-sm">
              <Link
                to="/glossary"
                style={{ color: "hsl(var(--brand-text))", fontWeight: 600 }}
              >
                Browse the full library &rarr;
              </Link>
            </p>
          </div>
        </section>
      )}

      {/* Anxiety Maps, between Recent articles and the FAQ */}
      <section
        className="border-t"
        style={{ borderColor: "hsl(var(--border))" }}
      >
        <div className="reveal mx-auto max-w-[1200px] px-6 py-14">
          <h3
            className="text-xs font-semibold uppercase tracking-widest mb-3"
            style={{ color: "hsl(var(--accent-ink))" }}
          >
            Anxiety maps
          </h3>
          <h2 className="font-serif text-3xl" style={{ marginTop: 0 }}>
            Anxiety Maps.
          </h2>
          <p
            className="mt-2 font-serif italic text-xl"
            style={{ color: "hsl(var(--muted-foreground))" }}
          >
            Patterns you can see, treatments that move them.
          </p>
          <p
            className="mt-4 max-w-[760px] text-lg leading-relaxed"
            style={{ color: "hsl(var(--muted-foreground))" }}
          >
            The Anxiety Maps are AnxietyResource's signature work: visual cluster
            pages that show how the experiences inside anxiety actually fit
            together. Each map has a central node and six to eight spokes
            radiating outward. Tap any node to read the page on that specific
            experience.
          </p>

          {featuredPanicMap && (
            <Link
              to="/maps/panic"
              className="group card-lift mt-8 flex flex-col items-center gap-6 p-8 hover:no-underline md:flex-row md:items-center"
              style={{
                background: "hsl(var(--card))",
                border: "1px solid hsl(var(--border))",
                borderTop: "3px solid hsl(var(--brand))",
                borderRadius: "var(--radius)",
              }}
              data-testid="featured-panic-map"
            >
              <div style={{ flex: "0 0 auto" }} className="w-[180px]">
                <MapPreview map={featuredPanicMap} maxWidth={180} />
              </div>
              <div>
                <span
                  className="block text-[0.7rem] font-semibold uppercase tracking-widest"
                  style={{ color: "hsl(var(--brand-text))" }}
                >
                  Start here
                </span>
                <h3
                  className="font-serif text-2xl text-foreground"
                  style={{ marginTop: "0.5rem" }}
                >
                  The Panic Map.
                </h3>
                <p
                  className="mt-2 text-lg leading-relaxed"
                  style={{ color: "hsl(var(--muted-foreground))" }}
                >
                  If you're not sure where to begin, panic is the cluster most
                  people recognize first.
                </p>
                <span className="brand-pill mt-5 inline-block">
                  Open the Panic Map{" "}
                  <span className="link-arrow" aria-hidden="true">
                    &rarr;
                  </span>
                </span>
              </div>
            </Link>
          )}

          <h3
            className="font-serif text-2xl mt-12"
            style={{ marginTop: "3rem" }}
          >
            Cluster Maps
          </h3>
          <div className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {clusterMaps.map((map) => (
              <MapCard key={map.slug} map={map} />
            ))}
          </div>
          <p className="mt-8 text-sm">
            <Link
              to="/maps"
              style={{ color: "hsl(var(--brand-text))", fontWeight: 600 }}
            >
              See all nine Anxiety Maps &rarr;
            </Link>
          </p>
        </div>
      </section>

      {/* Homepage FAQ, emits FAQPage schema via Seo jsonLd above */}
      <section
        className="border-t"
        style={{ borderColor: "hsl(var(--border))" }}
      >
        <div className="reveal mx-auto max-w-[760px] px-6 py-14">
          <h2 className="font-serif text-3xl" style={{ marginTop: 0 }}>
            Common questions about anxiety
          </h2>
          <div className="mt-2">
            <FAQSection items={HOMEPAGE_FAQ} />
          </div>
        </div>
      </section>

      {/* Part of The Shrink Network, a calm aside */}
      <section
        className="border-t"
        style={{ borderColor: "hsl(var(--border))" }}
      >
        <div className="reveal mx-auto max-w-[760px] px-6 py-14">
          <div
            className="bg-card p-8"
            style={{
              border: "1px solid hsl(var(--border))",
              borderTop: "3px solid hsl(var(--brand))",
              borderRadius: "var(--radius)",
            }}
          >
            <span className="brand-pill mb-4">About the network</span>
            <h2 className="font-serif text-2xl" style={{ marginTop: "0.5rem" }}>
              Why is there a whole site just for anxiety?
            </h2>
            <div
              className="mt-4 space-y-1 text-[17px] leading-relaxed"
              style={{ color: "hsl(var(--muted-foreground))" }}
            >
              <p>Mental health isn't one thing.</p>
              <p>Anxiety is its own world.</p>
              <p>
                It has its own physiology, its own treatments, and its own way
                of taking over a life.
              </p>
              <p>
                A site that tries to be about every mental health topic ends up
                being about none of them in depth.
              </p>
            </div>
            <p
              className="mt-5 italic text-[17px] leading-relaxed"
              style={{ color: "hsl(var(--foreground))" }}
            >
              AnxietyResource is the dedicated anxiety publication in The Shrink
              Network. The rest of the network covers everything else.
            </p>
            <blockquote
              className="mt-6 pl-5 text-[17px] leading-relaxed"
              style={{
                borderLeft: "3px solid hsl(var(--brand))",
                color: "hsl(var(--foreground))",
              }}
            >
              <p>
                <a
                  href={networkUrl(SHRINK_NETWORK_URL, "home_master_message")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline decoration-1 underline-offset-2"
                  style={{ color: "inherit" }}
                >
                  The Shrink Network
                </a>
                {NETWORK_MASTER_MESSAGE.slice("The Shrink Network".length)}
              </p>
              <p className="mt-3 font-serif italic text-lg">
                Your mind. Understood.
              </p>
            </blockquote>
            <a
              href="https://shrinknetwork.com/why/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-5 text-sm font-semibold hover:no-underline"
              style={{ color: "hsl(var(--brand-text))" }}
              data-testid="link-network-why"
            >
              Read why so many sites{" "}
              <span className="link-arrow" aria-hidden="true">
                &rarr;
              </span>
            </a>
          </div>
        </div>
      </section>

      <section
        className="border-t"
        style={{ borderColor: "hsl(var(--border))" }}
      >
        <div className="reveal mx-auto max-w-[1200px] px-6 py-14">
          <Link
            to="/resources/books"
            className="group card-lift block bg-card hover:no-underline"
            style={{
              border: "1px solid hsl(var(--border))",
              borderTop: "3px solid hsl(var(--brand))",
              borderRadius: "var(--radius)",
              padding: "1.75rem",
            }}
            data-testid="card-additional-reading"
          >
            <span className="brand-pill">Additional reading</span>
            <h2
              className="font-serif text-2xl mt-3"
              style={{ marginTop: "0.75rem" }}
            >
              The book series behind the framework
            </h2>
            <p
              className="mt-2 text-base leading-relaxed"
              style={{ color: "hsl(var(--muted-foreground)) " }}
            >
              The site&apos;s medical reviewer, Shariq Refai, MD, MBA, is the
              author of a three-book series on anxiety, overthinking, panic, and
              the patterns the mind builds under stress. The books aren't sold
              here. A short overview of each lives in Resources.
            </p>
            <span
              className="inline-block mt-4 text-sm font-semibold"
              style={{ color: "hsl(var(--accent-ink))" }}
            >
              Browse the books{" "}
              <span className="link-arrow" aria-hidden="true">
                &rarr;
              </span>
            </span>
          </Link>
        </div>
      </section>

      <section
        className="border-t"
        style={{ borderColor: "hsl(var(--border))" }}
        data-testid="home-get-care"
      >
        <div className="reveal mx-auto max-w-[1200px] px-6 py-14">
          <h2 className="font-serif text-2xl" style={{ marginTop: 0 }}>
            When you're ready for care
          </h2>
          <p
            className="mt-3 max-w-[68ch] text-base leading-relaxed"
            style={{ color: "hsl(var(--muted-foreground))" }}
          >
            Reading only goes so far. Once anxiety is interfering with sleep,
            work, or relationships, an evaluation with a psychiatrist is the
            step that turns a plan into treatment. shrinkMD is the network's
            multistate telepsychiatry practice, founded by this site's
            medical reviewer.
          </p>
          <div className="mt-6">
            <a
              href={networkUrl(CARE_ANXIETY_URL, "home-get-care")}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-5 py-3 text-sm font-semibold hover:no-underline btn-lift"
              style={{
                background: "#B23A12",
                color: "#ffffff",
                borderRadius: "var(--radius)",
              }}
              data-testid="home-get-care-cta"
            >
              Get anxiety treatment at shrinkMD
            </a>
          </div>
          <p
            className="mt-3 text-xs leading-relaxed"
            style={{ color: "hsl(var(--muted-foreground))" }}
          >
            shrinkMD is one option among many. AnxietyResource takes no
            referral fees. See our{" "}
            <Link to="/editorial-standards#conflicts-of-interest">
              editorial standards
            </Link>{" "}
            for the full relationship disclosure.
          </p>
        </div>
      </section>

      {conditionsFeatured.length > 0 && (
        <section
          className="border-t"
          style={{ borderColor: "hsl(var(--border))" }}
        >
          <div className="reveal mx-auto max-w-[1200px] px-6 py-14">
            <div className="flex items-baseline justify-between flex-wrap gap-3 mb-6">
              <h2 className="font-serif text-2xl" style={{ marginTop: 0 }}>
                Conditions, explained
              </h2>
              <Link
                to="/conditions"
                className="text-sm font-semibold"
                style={{ color: "hsl(var(--accent-ink))" }}
                data-testid="link-conditions-all"
              >
                All conditions &rarr;
              </Link>
            </div>
            <p
              className="max-w-[68ch] text-base leading-relaxed mb-8"
              style={{ color: "hsl(var(--muted-foreground))" }}
            >
              Anxiety is one feeling. The conditions that fall under the
              broader anxiety umbrella are several, each with its own
              presentation and its own evidence-based treatments. These
              overviews are written for the reader who wants the bigger
              picture.
            </p>
            <div className="grid gap-5 md:grid-cols-3">
              {conditionsFeatured.map(({ article, lede }) => (
                <Link
                  key={article.slug}
                  to={`/conditions/${article.slug}`}
                  className="group card-lift block bg-card p-6 hover:no-underline"
                  style={{
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "var(--radius)",
                  }}
                  data-testid={`card-home-condition-${article.slug}`}
                >
                  <span className="brand-pill mb-2">Condition</span>
                  <h3
                    className="font-serif text-lg text-foreground"
                    style={{ marginTop: "0.5rem" }}
                  >
                    {article.title}
                  </h3>
                  <p
                    className="mt-2 text-sm leading-relaxed"
                    style={{ color: "hsl(var(--muted-foreground))" }}
                  >
                    {lede}
                  </p>
                  <span
                    className="inline-block mt-4 text-sm font-semibold"
                    style={{ color: "hsl(var(--accent-ink))" }}
                  >
                    Read the overview{" "}
                    <span className="link-arrow" aria-hidden="true">
                      &rarr;
                    </span>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
